import { httpGet, httpPost, httpPut } from '../http.service'

export interface ILeaveTypeConfig {
	key: string
	label: string
	defaultEntitlement: number
	requiresAttachment: boolean
	enabled: boolean
	carryForward?: boolean
	carryForwardCap?: number
}

class LeaveService {
	async fetchListing(params?: {
		status?: 'Pending' | 'Approved' | 'Rejected' | 'Cancelled'
		leaveType?: string
		requesterId?: string
		page?: number
		limit?: number
	}) {
		return await httpGet<any>('api/v1/admin/leaves', params)
	}

	async detail(id: string) {
		return await httpGet<any>(`api/v1/admin/leaves/${id}`)
	}

	async approve(id: string, comment?: string) {
		return await httpPut<any>(`api/v1/admin/leaves/${id}/approve`, {
			comment,
		})
	}

	async reject(id: string, comment?: string) {
		return await httpPut<any>(`api/v1/admin/leaves/${id}/reject`, {
			comment,
		})
	}

	async getBalance(staffId: string, year?: number) {
		return await httpGet<any>(
			`api/v1/admin/leaves/balance/${staffId}`,
			year ? { year } : undefined,
		)
	}

	async setBalance(
		staffId: string,
		entitlements: {
			year?: number
			Annual?: number
			Sick?: number
			Emergency?: number
			Unpaid?: number
			Other?: number
		},
	) {
		return await httpPut<any>(`api/v1/admin/leaves/balance/${staffId}`, entitlements)
	}

	async getHolidays(year?: number) {
		return await httpGet<any>('api/v1/admin/leaves/holidays', year ? { year } : undefined)
	}

	async setHolidays(
		year: number,
		holidays: Array<{
			date: string
			name: string
			translations?: Record<string, string>
			variable?: boolean
		}>,
	) {
		return await httpPut<any>('api/v1/admin/leaves/holidays', { year, holidays })
	}

	async getLeaveSettings() {
		return await httpGet<any>('api/v1/admin/leaves/settings')
	}

	async setLeaveSettings(types: ILeaveTypeConfig[]) {
		return await httpPut<any>('api/v1/admin/leaves/settings', { types })
	}

	async bulkSetBalance(body: { year?: number; staffIds?: string[] }) {
		return await httpPost<any>('api/v1/admin/leaves/balance/bulk', body)
	}

	async syncBalanceDefaults(year?: number) {
		return await httpPost<any>(
			'api/v1/admin/leaves/balance/sync-defaults',
			year ? { year } : {},
		)
	}

	async exportBalances(params: { year?: number; departmentId?: string }) {
		return await httpGet<any>('api/v1/admin/leaves/balance-export', params)
	}

	async importBalances(body: { year: number; records: Array<Record<string, any>> }) {
		return await httpPost<any>('api/v1/admin/leaves/balance-import', body)
	}

	async getCarryForwardRuns(params?: { year?: number; page?: number; limit?: number }) {
		return await httpGet<any>('api/v1/admin/leaves/carry-forward/runs', params)
	}

	async getCarryForwardAudit(params?: {
		staffId?: string
		year?: number
		page?: number
		limit?: number
	}) {
		return await httpGet<any>('api/v1/admin/leaves/carry-forward/audit', params)
	}

	async triggerCarryForward(body: { year: number; dryRun?: boolean }) {
		return await httpPost<any>('api/v1/admin/leaves/balance/carry-forward', {
			toYear: body.year,
			fromYear: body.year - 1,
			dryRun: body.dryRun,
		})
	}
}

const leaveService = new LeaveService()

export { leaveService }
